/**
 * Validation constants for Ovie Health settings wire DTOs.
 *
 * These constants reflect limits enforced by the upstream Ovie API.
 * Keep in sync with the API spec when limits change.
 */

/**
 * Maximum number of booking policies allowed per practice.
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_BOOKING_POLICIES = 100;

/**
 * Maximum length of a booking policy `policy` value (free text).
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_BOOKING_POLICY_LENGTH = 60;

/**
 * Maximum length of a booking policy `instruction` value.
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_BOOKING_POLICY_INSTRUCTION_LENGTH = 500;

/**
 * Maximum length of a treatment alias `description` value.
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_TREATMENT_ALIAS_DESCRIPTION_LENGTH = 500;

/**
 * Maximum length of a treatment alias `name` value.
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_TREATMENT_ALIAS_NAME_LENGTH = 500;

/**
 * Maximum length of the greeting `startMessage` and `multilocationMessage` values.
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_GREETING_MESSAGE_LENGTH = 2_000;

/**
 * Maximum length of the messages `agentNotes` value.
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_AGENT_NOTES_LENGTH = 10_000;

/**
 * Maximum length of the call handling `transferSuspensionMessage` value.
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_TRANSFER_SUSPENSION_MESSAGE_LENGTH = 1_000;

/**
 * Maximum length of the communications `notificationEmail` value.
 * Exceeding this is rejected upstream with 400.
 */
export const SETTINGS_MAX_NOTIFICATION_EMAIL_LENGTH = 320;

/**
 * Inclusive bounds for the numeric call handling settings. Ovie rejects values
 * outside these ranges with 400 — note the minimum is 1, not 0, so "unlimited"
 * must be expressed by clearing the field rather than entering zero.
 */
export const SETTINGS_MIN_CALL_HANDLING_VALUE = 1;
export const SETTINGS_MAX_TRANSFERS_BEFORE_SUSPENSION = 20;
export const SETTINGS_MAX_TRANSFER_SUSPENSION_MINUTES = 1_440;
export const SETTINGS_MAX_REPEAT_CALLER_LIMIT = 50;
export const SETTINGS_MAX_REPEAT_CALLER_WINDOW_MINUTES = 1_440;

/**
 * Ovie requires transfer numbers in strict E.164: a leading `+`, a non-zero
 * country code, then 6-14 further digits. Spaces, parentheses and hyphens are
 * rejected upstream with 400, so local formats such as `(03) 9410 1077` must be
 * entered as `+61394101077`.
 */
export const SETTINGS_TRANSFER_PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

/**
 * Reserved separator used in treatment alias display names.
 * Exactly three characters: space (U+0020) + em dash (U+2014) + space (U+0020).
 * Any alias `name` containing this sequence is treated as a reserved/composite
 * alias by the Ovie API and must not be user-authored verbatim.
 */
export const SETTINGS_TREATMENT_ALIAS_RESERVED_SEPARATOR = ' \u2014 ';

/**
 * Maximum number of bookable treatment IDs per practice.
 * Ovie rejects more than 500 with 400 — truncate and log, never throw.
 */
export const SETTINGS_MAX_BOOKABLE_TREATMENT_IDS = 500;

/**
 * Maximum number of treatment alias keys per PATCH request (Ovie cap).
 * Exceeding this is rejected upstream — cap at 200 keys before sending.
 */
export const SETTINGS_MAX_TREATMENT_ALIAS_KEYS = 200;
